import { useEffect, useState } from "react";
import { Lightbulb, Loader2, X, ArrowRight, RefreshCw } from "lucide-react";
import {
    adminListOpportunityCards,
    adminDismissOpportunity,
    adminUseOpportunityTopic,
    adminOpportunityRunNow,
} from "@/lib/api";
import { toast } from "sonner";

/**
 * OpportunityCards — surfaces the weekly auto-scanned topic suggestions.
 *
 * Shows up to 3 pending cards. Each card displays its 5 suggested topics
 * with rationales. Admin can click "Use" on a topic → it's flipped to
 * `used` and the parent's `onUseTopic` callback receives the topic so it
 * can prefill the generation form.
 */
export default function OpportunityCards({ onUseTopic }) {
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [scanning, setScanning] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await adminListOpportunityCards({ status: "pending", limit: 5 });
            setCards(r.items || []);
        } catch {
            toast.error("Could not load opportunity cards");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const runNow = async () => {
        setScanning(true);
        try {
            const r = await adminOpportunityRunNow();
            if (r.ran) {
                toast.success(`Fresh opportunity card created · ${r.card?.topics?.length || 0} topics`);
                await load();
            } else {
                toast.info("Scan completed — no new card (already exists this week).");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Scan failed");
        } finally {
            setScanning(false);
        }
    };

    const dismiss = async (card_id) => {
        try {
            await adminDismissOpportunity(card_id);
            setCards((p) => p.filter((c) => c.card_id !== card_id));
            toast.success("Dismissed");
        } catch {
            toast.error("Dismiss failed");
        }
    };

    const applyTopic = async (card_id, topic_index) => {
        try {
            const r = await adminUseOpportunityTopic(card_id, topic_index);
            onUseTopic?.(r.topic);
            setCards((p) => p.filter((c) => c.card_id !== card_id));
            toast.success(`Topic "${r.topic.title_idea}" applied to form`);
        } catch {
            toast.error("Could not apply topic");
        }
    };

    if (loading) {
        return (
            <div className="bl-card p-4 text-center" data-testid="opp-loading">
                <Loader2 className="h-4 w-4 animate-spin inline-block text-bl-muted" />
            </div>
        );
    }

    if (!cards.length) {
        // Compact empty-state with manual run button so admins know the
        // weekly scanner exists even when no card is pending.
        return (
            <div
                className="bl-card p-4 text-sm text-bl-muted flex items-center justify-between gap-3 flex-wrap"
                data-testid="opp-empty"
            >
                <div className="flex items-center gap-2">
                    <Lightbulb className="h-4 w-4 text-[var(--bl-amber)]" />
                    No new opportunity cards. The weekly scanner runs every
                    Monday — or run it now.
                </div>
                <button
                    type="button"
                    onClick={runNow}
                    disabled={scanning}
                    className="bl-btn-ghost text-xs gap-1.5"
                    data-testid="opp-run-now"
                >
                    {scanning ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                        <RefreshCw className="h-3 w-3" />
                    )}
                    Run scan now
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-3" data-testid="opp-cards">
            {cards.map((card) => (
                <div
                    key={card.card_id}
                    className="bl-card p-5 border-l-4 border-[var(--bl-amber)]"
                    data-testid={`opp-card-${card.card_id}`}
                >
                    <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                            <div className="flex items-center gap-2">
                                <Lightbulb className="h-4 w-4 text-[var(--bl-amber)]" />
                                <h3 className="font-display font-semibold text-lg text-bl">
                                    Weekly content opportunities · {card.iso_week}
                                </h3>
                            </div>
                            <p className="text-xs text-bl-muted mt-1">
                                Built from {card.signal_meta?.feature_request_count || 0} recent feature requests +{" "}
                                {(card.signal_meta?.low_view_articles || []).length} low-traffic articles.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => dismiss(card.card_id)}
                            className="bl-btn-ghost text-xs gap-1 px-2 py-1"
                            data-testid={`opp-dismiss-${card.card_id}`}
                            aria-label="Dismiss card"
                        >
                            <X className="h-3 w-3" />
                            Dismiss
                        </button>
                    </div>
                    <ul className="space-y-2" data-testid={`opp-topics-${card.card_id}`}>
                        {(card.topics || []).map((t, i) => (
                            <li
                                key={i}
                                className="flex items-start gap-3 p-3 rounded bg-bl-bg"
                                data-testid={`opp-topic-${card.card_id}-${i}`}
                            >
                                <div className="flex-1 min-w-0 text-sm">
                                    <div className="font-medium text-bl">{t.title_idea}</div>
                                    <div className="text-xs text-bl-muted">
                                        <code>{t.primary_keyword}</code> · {t.category} · {t.search_intent}
                                    </div>
                                    {t.rationale && (
                                        <div className="text-xs text-bl-muted italic mt-1">
                                            {t.rationale}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => applyTopic(card.card_id, i)}
                                    className="bl-btn-primary text-xs gap-1 px-2 py-1 shrink-0"
                                    data-testid={`opp-use-${card.card_id}-${i}`}
                                >
                                    Use
                                    <ArrowRight className="h-3 w-3" />
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
}
